import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking");
  const [invoiceId, setInvoiceId] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/pay/status/${sessionId}`);
        setInvoiceId(data.invoice_id || "");
        if (data.status === "paid") {
          setStatus("paid");
        } else if (data.status === "expired") {
          setStatus("expired");
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus("timeout");
        }
      } catch {
        if (attempts < 5) setTimeout(() => setAttempts(a => a + 1), 2000);
        else setStatus("error");
      }
    };
    poll();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center">
        <h1 className="font-outfit font-bold text-2xl text-white mb-2">WeDoWeDo OÜ</h1>
        <p className="text-xs text-neutral-500 font-mono tracking-[0.15em] mb-10">PAYMENT</p>

        {status === "checking" && (
          <div data-testid="payment-checking" className="bg-[#0a0a0a] border border-white/10 rounded-sm p-10">
            <Loader2 className="w-10 h-10 text-[#00F0FF] animate-spin mx-auto mb-4" />
            <p className="text-white font-manrope">Verifying payment...</p>
            <p className="text-xs text-neutral-500 mt-2">This may take a few seconds</p>
          </div>
        )}

        {status === "paid" && (
          <div data-testid="payment-success" className="bg-[#0a0a0a] border border-green-500/20 rounded-sm p-10">
            <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="font-outfit font-bold text-xl text-white mb-2">Payment Successful</h2>
            <p className="text-neutral-400 font-manrope text-sm">Thank you! Your payment has been received and the invoice has been marked as paid.</p>
            {invoiceId && <p className="text-xs text-neutral-600 font-mono mt-4">Invoice: {invoiceId.slice(0, 8).toUpperCase()}</p>}
          </div>
        )}

        {(status === "error" || status === "timeout" || status === "expired") && (
          <div data-testid="payment-error" className="bg-[#0a0a0a] border border-red-500/20 rounded-sm p-10">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h2 className="font-outfit font-bold text-xl text-white mb-2">
              {status === "expired" ? "Session Expired" : "Payment Status Unknown"}
            </h2>
            <p className="text-neutral-400 font-manrope text-sm">
              {status === "expired" ? "Your payment session has expired. Please try again." : "We couldn't verify your payment. If you were charged, please contact us."}
            </p>
          </div>
        )}

        <Link to="/" className="inline-block mt-8 text-sm text-neutral-500 hover:text-[#00F0FF] transition-colors">
          &larr; Back to wedowedo.com
        </Link>
      </div>
    </div>
  );
}
